import { Button, Checkbox, Modal, Radio, Spin, Typography } from 'antd';

import type { ModelTag } from '@app/api/Tags';
import { RECORD_TAGS_MODAL_PF as PF } from '@app/components/RecordModal/components/RecordTagsModal/data';
import { useRecordTagsModal } from '@app/components/RecordModal/components/RecordTagsModal/hooks/useRecordTagsModal';

import type { FC } from 'react';

import './RecordTagsModal.scss';

const { Title } = Typography;

interface RecordTagsGroupProps {
  title: string;
  tags: ModelTag[];
  selectedTagIds: number[];
  disabled: boolean;
  onToggle(tagId: number, checked: boolean): void;
}

const RecordTagsGroup: FC<RecordTagsGroupProps> = ({
  title,
  tags,
  selectedTagIds,
  disabled,
  onToggle,
}) => {
  if (!tags.length) return null;

  return (
    <div className={`${PF}__group`}>
      <Title level={5} className={`${PF}__group-title`}>
        {title}
      </Title>
      <div className={`${PF}__tags`}>
        {tags.map(({ Id, Name }) => (
          <Checkbox
            key={Id}
            className={`${PF}__tag`}
            checked={selectedTagIds.includes(Id)}
            disabled={disabled}
            onChange={({ target: { checked } }) => onToggle(Id, checked)}
          >
            {Name}
          </Checkbox>
        ))}
      </div>
    </div>
  );
};

export const RecordTagsModal: FC = () => {
  const {
    isOpen,
    isLoading,
    isSaving,
    clientTypes,
    selectedClientType,
    recordTypes,
    selectedRecordTypeKey,
    channelTags,
    priceTags,
    selectedTagIds,
    toggleTag,
    skip,
    saveAndContinue,
  } = useRecordTagsModal();

  return (
    <Modal
      open={isOpen}
      className={`${PF}__dialog`}
      data-testid="record-tags-modal"
      closable={false}
      maskClosable={false}
      footer={
        <div className={`${PF}__footer`}>
          <Button
            disabled={isLoading || isSaving}
            onClick={() => void skip()}
            data-testid="record-tags-skip"
          >
            Пропустить
          </Button>
          <Button
            type="primary"
            loading={isSaving}
            disabled={isLoading}
            onClick={() => void saveAndContinue()}
            data-testid="record-tags-save"
          >
            Сохранить и продолжить
          </Button>
        </div>
      }
    >
      {isLoading ? (
        <div className={`${PF}__loading`}>
          <Spin />
        </div>
      ) : (
        <>
          <div className={`${PF}__group`}>
            <Title level={5} className={`${PF}__group-title`}>
              Тип клиента
            </Title>
            {/* Тип клиента, как и тип записи, считает бэк — все варианты
                показаны, выбор заблокирован (issue/11351). */}
            <Radio.Group
              value={selectedClientType}
              disabled
              className={`${PF}__client-types`}
              data-testid="record-tags-client-type"
            >
              {clientTypes.map(({ value, label }) => (
                <Radio
                  key={value}
                  value={value}
                  className={`${PF}__client-type`}
                >
                  {label}
                </Radio>
              ))}
            </Radio.Group>
          </div>

          <div className={`${PF}__group`}>
            <Title level={5} className={`${PF}__group-title`}>
              Тип записи
            </Title>
            {/* Тип записи считает бэк, менеджер его только сверяет — группа
                показана целиком и заблокирована (ТЗ us/11144, п. 4.2). */}
            <Radio.Group
              value={selectedRecordTypeKey}
              disabled
              className={`${PF}__record-types`}
              data-testid="record-tags-record-type"
            >
              {recordTypes.map(({ Key, Name }) => (
                <Radio key={Key} value={Key} className={`${PF}__record-type`}>
                  {Name}
                </Radio>
              ))}
            </Radio.Group>
          </div>

          <RecordTagsGroup
            title="Канал обращения"
            tags={channelTags}
            selectedTagIds={selectedTagIds}
            disabled={isSaving}
            onToggle={toggleTag}
          />

          <RecordTagsGroup
            title="Стоимость"
            tags={priceTags}
            selectedTagIds={selectedTagIds}
            disabled={isSaving}
            onToggle={toggleTag}
          />
        </>
      )}
    </Modal>
  );
};
